/**
 * Edge-swipe gestures for touch layouts.
 *
 * Tablet operators keep both hands on the slate, so the shell's off-canvas
 * navigation opens with a swipe in from the LEFT edge and Play Entry's screen
 * switcher with a swipe up from the BOTTOM edge — no chrome has to stay on
 * screen for either. A gesture only counts when it STARTS inside the edge band
 * (so ordinary scrolling and card taps in the middle of the screen never fire)
 * and travels far enough, mostly along its axis, before the finger lifts.
 *
 * Touch events only: a mouse never produces them, so desktop is unaffected,
 * and a `pointer: fine` stylus is left alone on purpose.
 */
import { useEffect } from 'react';

export type SwipeEdge = 'left' | 'bottom';

export interface EdgeSwipeOptions {
  edge: SwipeEdge;
  onSwipe: () => void;
  /** Switch the listener off (e.g. on desktop) without unmounting. */
  isEnabled?: boolean;
  /** How close to the edge (px) the touch must start. */
  edgeBandPx?: number;
  /** How far (px) the finger must travel along the axis. */
  thresholdPx?: number;
}

export const EDGE_SWIPE_BAND_PX = 28;
export const EDGE_SWIPE_THRESHOLD_PX = 56;

interface TrackedTouch {
  x: number;
  y: number;
}

/** Pure decision so the gesture maths is testable without touch events. */
export function isEdgeSwipe(
  edge: SwipeEdge,
  start: TrackedTouch,
  end: TrackedTouch,
  viewport: { width: number; height: number },
  edgeBandPx = EDGE_SWIPE_BAND_PX,
  thresholdPx = EDGE_SWIPE_THRESHOLD_PX
): boolean {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  if (edge === 'left') {
    return start.x <= edgeBandPx && dx >= thresholdPx && Math.abs(dy) < Math.abs(dx);
  }
  return (
    start.y >= viewport.height - edgeBandPx && -dy >= thresholdPx && Math.abs(dx) < Math.abs(dy)
  );
}

export function useEdgeSwipe({
  edge,
  onSwipe,
  isEnabled = true,
  edgeBandPx = EDGE_SWIPE_BAND_PX,
  thresholdPx = EDGE_SWIPE_THRESHOLD_PX,
}: EdgeSwipeOptions): void {
  useEffect(() => {
    if (!isEnabled || typeof window === 'undefined') return undefined;
    let start: TrackedTouch | null = null;

    const onTouchStart = (event: TouchEvent): void => {
      const touch = event.touches[0];
      if (touch === undefined || event.touches.length !== 1) {
        start = null;
        return;
      }
      start = { x: touch.clientX, y: touch.clientY };
    };
    const onTouchEnd = (event: TouchEvent): void => {
      const touch = event.changedTouches[0];
      if (start === null || touch === undefined) return;
      const end = { x: touch.clientX, y: touch.clientY };
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      const began = start;
      start = null;
      if (isEdgeSwipe(edge, began, end, viewport, edgeBandPx, thresholdPx)) onSwipe();
    };
    const onTouchCancel = (): void => {
      start = null;
    };

    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('touchcancel', onTouchCancel, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [edge, onSwipe, isEnabled, edgeBandPx, thresholdPx]);
}
